function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputBase from '@material-ui/core/InputBase';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import isBoolean from 'lodash/isBoolean';
import withStyles from '@material-ui/core/styles/withStyles';
import FormLabel from '../form/FormLabel';
import { Grid } from '../index';

var styles = function styles(theme) {
  var light = theme.palette.type === 'light';
  var borderColor = light ? 'rgba(0, 0, 0, 0.42)' : 'rgba(255, 255, 255, 0.7)';
  return {
    // This is a bit confusing when you first look at it, but what we're doing here
    // is treating the subcomponent InputBase as the root component so that we can
    // take advantage of it's named css rules.
    //
    // https://material-ui.com/api/input-base/#css
    root: {
      backgroundColor: theme.palette.common.white,
      border: "1px solid ".concat(borderColor),
      borderRadius: 4,
      overflow: 'hidden',
      transition: theme.transitions.create('border-color', {
        duration: theme.transitions.duration.shorter
      })
    },
    focused: {
      borderColor: theme.palette.primary.main
    },
    error: {
      borderColor: theme.palette.error.border
    },
    input: {
      padding: '10px 12px'
    },
    // From here on these are custom styles for components which are not descendents
    // of InputBase.
    formLabel: {
      marginBottom: 8
    },
    formHelperText: {
      lineHeight: '1.5'
    }
  };
};
/**
 * The `<TextField>` component allows users to enter and edit text.
 *
 * ```js
  * import { TextField } from '@theconversation/ui'
  *
 * <TextField
 *   helperText='Enter your full name'
 *   label='Name'
 *   onChange={alert('change')}
 *   placeholder='Jane Doe'
 * />
 * ```
 */


export var TextField = function TextField(_ref) {
  var autoComplete = _ref.autoComplete,
      classes = _ref.classes,
      disabled = _ref.disabled,
      error = _ref.error,
      fullWidth = _ref.fullWidth,
      helperText = _ref.helperText,
      id = _ref.id,
      label = _ref.label,
      required = _ref.required,
      maxLength = _ref.maxLength,
      maxLengthCountText = _ref.maxLengthCountText,
      other = _objectWithoutProperties(_ref, ["autoComplete", "classes", "disabled", "error", "fullWidth", "helperText", "id", "label", "required", "maxLength", "maxLengthCountText"]);

  var helperTextId = helperText && id ? "".concat(id, "-helper-text") : undefined; // Max length validation with character count

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      characterCount = _useState2[0],
      setCharacterCount = _useState2[1]; // This will be true if we're violating the length validation


  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      excessChars = _useState4[0],
      setExcessChars = _useState4[1];

  var hasLengthValidation = maxLength > 0;
  var maxValidationHelperText = "".concat(characterCount, "/").concat(maxLength, " ").concat(maxLengthCountText);
  var showHelperText = helperText || hasLengthValidation;
  useEffect(function () {
    if (hasLengthValidation) {
      var valueProp = other.value,
          defaultValueProp = other.defaultValue;
      var inputValue = valueProp || defaultValueProp;

      if (inputValue) {
        setCharacterCount(inputValue.length);
      }
    }
  }, []);

  var onChange = function onChange(event) {
    // Call the `other.onChange` prop function, if any
    if (other.onChange) {
      other.onChange(event);
    }

    if (hasLengthValidation) {
      // We don't want to miscount controlled inputs
      var valueProp = other.value;

      if (valueProp) {
        setCharacterCount(valueProp.length);
        return;
      }

      var currentCharacterCount = event.target.value.length;
      setCharacterCount(currentCharacterCount);
      setExcessChars(currentCharacterCount > maxLength);
    }
  }; // Sugar for setting autoComplete using a boolean value.


  if (isBoolean(autoComplete)) {
    autoComplete = autoComplete ? 'on' : 'off';
  } // Separate MUI classes to pass to MUI


  var formLabel = classes.formLabel,
      formHelperText = classes.formHelperText,
      muiClasses = _objectWithoutProperties(classes, ["formLabel", "formHelperText"]); // We want to respect both `error` and `excessChars` when determining whether
  // to show the component in an error state


  var anyErrors = error || excessChars;
  return React.createElement(FormControl, {
    "aria-describedby": helperTextId,
    disabled: disabled,
    error: anyErrors,
    fullWidth: fullWidth
  }, label && React.createElement(FormLabel, {
    className: classes.formLabel,
    htmlFor: id,
    required: required
  }, label), React.createElement(InputBase, _extends({}, other, {
    classes: muiClasses,
    autoComplete: autoComplete,
    id: id,
    onChange: onChange
  })), showHelperText && React.createElement(FormHelperText, {
    id: helperTextId,
    className: classes.formHelperText
  }, React.createElement(Grid, {
    component: "span",
    justify: "space-between",
    container: true,
    spacing: 2
  }, React.createElement(Grid, {
    component: "span",
    item: true,
    xs: true
  }, helperText), hasLengthValidation && React.createElement(Grid, {
    component: "span",
    item: true,
    xs: true,
    style: {
      textAlign: 'right'
    }
  }, maxValidationHelperText))));
};
TextField.propTypes = {
  /**
   * The `autoComplete` value of the underlying `<input>` element. It can be
   * specified as either a boolean or string value.
   */
  autoComplete: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /**
   * Overrides the styles applied to the component.
   */
  classes: PropTypes.object,

  /**
   * A boolean value indicating whether the text field is disabled.
   */
  disabled: PropTypes.bool,

  /**
   * A boolean value indicating whether the text field is in an error state.
   */
  error: PropTypes.bool,

  /**
   * A boolean value indicating whether the text field should take up the full
   * width of its container.
   */
  fullWidth: PropTypes.bool,

  /**
   * The text displayed below the text field.
   */
  helperText: PropTypes.string,

  /**
   * The `id` of the underlying `<input>` element.
   */
  id: PropTypes.string,

  /**
   * The text displayed above the text field.
   */
  label: PropTypes.node,

  /**
   * The callback function called when the text field value changes.
   */
  onChange: PropTypes.func,

  /**
   * The callback function called when the text field loses focus.
   */
  onBlur: PropTypes.func,

  /**
   * The callback function called when the text field gains focus.
   */
  onFocus: PropTypes.func,

  /**
   * The text displayed inside text field before the user enters a value.
   */
  placeholder: PropTypes.string,

  /**
   * A boolean value indicating whether the text field is required to be filled
   * by the user.
   */
  required: PropTypes.bool,

  /**
   * The maximum number of characters allowed in the text field before it's marked as invalid.
   * Defaults to 0.
   *
   * Any value above 0 will add a character counter to the text field helper text
   */
  maxLength: PropTypes.number,

  /**
   * Text to be placed at the right side of character count. Example: `5/10 characters`
   */
  maxLengthCountText: PropTypes.string,

  /**
   * The type of the text field.
   */
  type: PropTypes.oneOf(['email', 'text', 'password', 'number']),

  /**
   * The value of the text field.
   */
  value: PropTypes.string
};
TextField.defaultProps = {
  autoComplete: false,
  disabled: false,
  error: false,
  fullWidth: false,
  maxLength: 0,
  maxLengthCountText: '',
  required: false,
  type: 'text'
};
export default withStyles(styles)(TextField);